import { Component, computed, signal } from '@angular/core';
import { NgForOf, NgIf } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DividerModule } from 'primeng/divider';
import { LucideIconsModule } from '@shared/lucide-icons.module';
import { DropdownModule } from 'primeng/dropdown';
import { ChipsModule } from 'primeng/chips';

import { CreateMascNode } from '@modules/campaigns/Domain/Nodes/CreateMascNode';
import { CreateMascConfig } from '@modules/campaigns/Domain/Interfaces/INodeConfig';
import { WorkflowStateService } from '@shared/state/workflow-state.service';
import { NodeType } from '@modules/campaigns/Domain/Enums/NodeType';
import { InspectorBase } from '@shared/components/joint-js/components/inspectors/inspector-base';
import { CopiesService } from '@modules/copies/service/copies.service';
import { CopyModel } from '@models/copies/copies.model';

@Component({
  selector: 'app-create-masc-inspector',
  standalone: true,
  imports: [
    NgIf,
    NgForOf,
    FormsModule,
    TranslateModule,
    DividerModule,
    LucideIconsModule,
    DropdownModule,
    ChipsModule,
  ],
  templateUrl: './create-masc-inspector.component.html',
  styleUrls: ['./create-masc-inspector.component.scss'],
})
export class CreateMascComponent extends InspectorBase<CreateMascNode> {
  selectedTemplateId = signal<string | null>(null);
  replyToEmailAddresses = signal<string[]>([]);
  templates = signal<CopyModel[]>([]);

  selectedTemplate = computed(() => {
    const templates = this.templates();
    const selectedId = this.selectedTemplateId();
    if (!templates.length || !selectedId) return null;

    return templates.find((t) => t.id === selectedId) ?? null;
  });

  previewSubject = computed(() => {
    const template = this.selectedTemplate();
    if (!template) return '';

    const subjectBlock = template.blocks?.find(
      (b) => b.type === 'subject' || b.alias === 'subject',
    );
    if (subjectBlock) return subjectBlock.translations?.[0]?.text ?? '';

    return '';
  });

  previewMessage = computed(() => {
    const template = this.selectedTemplate();
    if (!template) return '';

    const htmlBlock = template.blocks?.find(
      (b) =>
        b.type === 'emailHtml' ||
        b.type === 'html' ||
        b.alias === 'emailHtml' ||
        b.type === 'text' ||
        b.alias === 'text',
    );
    if (htmlBlock) return htmlBlock.translations?.[0]?.text ?? '';

    const textBlock = template.blocks?.find(
      (b) => b.type === 'emailText' || b.type === 'text' || b.alias === 'emailText',
    );
    if (textBlock) return textBlock.translations?.[0]?.text ?? '';

    return '';
  });

  selectedTemplateName = computed(() => {
    return this.selectedTemplate()?.name ?? '';
  });

  editTemplateUrl = computed(() => {
    const templateId = this.selectedTemplateId();
    if (!templateId) return null;

    const tenantId = this.route.snapshot.queryParams['tenant'];
    return `/copy/edit/${templateId}?tenant=${tenantId}`;
  });

  constructor(
    private workflow: WorkflowStateService,
    private translate: TranslateService,
    private copiesService: CopiesService,
    private route: ActivatedRoute,
  ) {
    super();
  }

  override get nodeValue(): CreateMascNode | null {
    const current = this.node();
    if (!current) return null;
    return current.type === NodeType.CreateMasc ? (current as CreateMascNode) : null;
  }

  getTabs() {
    return [
      {
        label: this.translate.instant('DATA.MODULES.INSPECTORS.CREATE_MASC.TABS.PREVIEW'),
        value: 'preview' as const,
      },
      {
        label: this.translate.instant('DATA.MODULES.INSPECTORS.CREATE_MASC.TABS.CONFIG'),
        value: 'config' as const,
      },
    ];
  }

  loadTemplates() {
    this.copiesService.searchCopies().subscribe((copies: CopyModel[]) => {
      const filteredTemplates = copies.filter((c) => c.type === 'certified_email');
      this.templates.set(filteredTemplates);
    });
  }

  override setNode(nodeSignal: any) {
    super.setNode(nodeSignal);
    this.loadTemplates();
  }

  override applyChanges() {
    if (this.mode() === 'preview') return;

    const node = this.nodeValue;
    if (!node) return;

    const selectedId = this.selectedTemplateId();

    const updatedConfig: CreateMascConfig = {
      ...(node.config as CreateMascConfig),
      copyId: selectedId || '',
      replyToEmailAddresses: [...new Set(this.replyToEmailAddresses())],
    };

    const updated = node.cloneWithConfig(updatedConfig) as CreateMascNode;
    const validation = updated.validate();
    this.errors.set(validation);

    if (!validation.length) {
      this.workflow.addOrUpdateNode(updated);
    }
  }

  onTemplateChange(id: string) {
    this.selectedTemplateId.set(id);
    this.validateImmediate();
    this.scheduleApplyChanges();
  }

  onReplyToChange(emails: string[]) {
    this.replyToEmailAddresses.set(emails);
    this.validateImmediate();
    this.scheduleApplyChanges();
  }

  private validateImmediate() {
    const node = this.nodeValue;
    if (!node) return;

    const updatedConfig: CreateMascConfig = {
      ...(node.config as CreateMascConfig),
      copyId: this.selectedTemplateId() || '',
      replyToEmailAddresses: [...new Set(this.replyToEmailAddresses())],
    };

    const updated = node.cloneWithConfig(updatedConfig) as CreateMascNode;
    const validation = updated.validate();
    this.errors.set(validation);
  }

  override refreshFromConfig() {
    const node = this.nodeValue;
    if (!node) return;

    const cfg = (node.config ?? {}) as CreateMascConfig;

    this.selectedTemplateId.set(cfg.copyId || null);
    this.replyToEmailAddresses.set(cfg.replyToEmailAddresses ?? []);

    this.errors.set([]);
  }
}
